// // Fixed NewTransactionService component
import { useState, useRef, useMemo } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { FormProvider } from "react-hook-form";
import FormTransactionServiceWrapper from "@domain/transaction-service/form-transaction-service-wrapper/form-transaction-service-wrapper";
import { LabelValue } from "@shared/models/label-value.interface";
import { TransactionServiceFormSchema } from "@factory/form/form.rule";
import { FormTransactionServiceWrapperRef } from "@domain/transaction-service/form-transaction-service-wrapper/form-transaction-service-wrapper";
import { useNewTransactionService } from "./new-transaction-service.hook";
import { GetTransactionDto } from "@api/api";
import { useNotificationContext } from "@shared/ui/notification/notification.context";

interface NewTransactionServiceProps {
  transactionData: GetTransactionDto;
  onServiceAdded: () => void;
}

export function NewTransactionService({
  transactionData,
  onServiceAdded,
}: NewTransactionServiceProps) {
  const [selectedChild, setSelectedChild] = useState<LabelValue | null>(null);
  const [serviceFormData, setServiceFormData] =
    useState<TransactionServiceFormSchema | null>(null);
  const serviceFormRef = useRef<FormTransactionServiceWrapperRef>(null);
  const { showWarning } = useNotificationContext();

  // // Use the hook you created
  const {
    isCreating,
    formMethod,
    handleSubmit,
    handleValidate,
    handleValidateError,
  } = useNewTransactionService();

  // // Extract children from existing transaction data - no API call needed!
  const childrenList = useMemo(() => {
    if (!transactionData?.service) return [];

    // // Get unique children from the services array
    const uniqueChildren = transactionData.service.reduce(
      (acc: LabelValue[], service: any) => {
        const memberId = service.member;
        const childLabel = `${service.first_name} ${service.last_name}`;
        const childValue = service.member;

        // // Check if this child is already in our list
        const exists = acc.find((child) => child.value === childValue);
        if (!exists) {
          acc.push(new LabelValue(childLabel, childValue));
        }

        return acc;
      },
      []
    );

    return uniqueChildren;
  }, [transactionData?.service?.length]);

  // // Handle form submission from the wrapper
  const handleServiceForm = (data: TransactionServiceFormSchema) => {
    if (!selectedChild) {
      showWarning("Please select a child first");
      return;
    }

    // // FORCE the member field since the form isn't setting it properly
    const enhancedFormData = {
      ...data,
      batch: transactionData.code, // // Links to existing transaction
      member: selectedChild.value, // // Force set the member from selectedChild
    };

    // // Store the form data for submission
    setServiceFormData(enhancedFormData);

    // // Submit immediately
    handleValidate(enhancedFormData);
    onServiceAdded(); // Close the sidebar after submission
  };

  const handleTriggerSubmit = () => {
    if (!selectedChild) {
      alert("Please select a child first");
      return;
    }

    // // Trigger the form submission in the wrapper
    serviceFormRef.current?.triggerSubmit();
  };

  const handleChildChange = (e: any) => {
    setSelectedChild(e.value);
    setServiceFormData(null); // Reset form data when child changes
  };

  return (
    <div className="add-transaction-service p-4">
      <h3 className="mb-4">Add Service to Transaction</h3>

      <div className="mb-4">
        <label className="block text-sm font-medium mb-2">
          Select Child (from existing services)
        </label>
        <Dropdown
          value={selectedChild}
          onChange={(e) => {
            // Find the full LabelValue object from childrenList
            const selectedChildObject = childrenList.find(
              (child) => child.value === e.value
            );

            setSelectedChild(selectedChildObject || null);
            setServiceFormData(null); // Reset form data when child changes
          }}
          options={childrenList}
          placeholder={
            childrenList.length > 0
              ? "Select a child to add service for"
              : "No children found in this transaction"
          }
          className="w-full"
          showClear
          disabled={childrenList.length === 0}
        />
        {childrenList.length === 0 && (
          <small className="text-gray-500 mt-1 block">
            No children found. Add children to this transaction first.
          </small>
        )}
      </div>

      {selectedChild && (
        <div className="mb-4">
          <h4 className="mb-2 text-medium font-semibold">
            Add New Service for:
          </h4>
          <div className="service-form-section bg-gray-50 p-4 rounded-lg border">
            {/* // Use the FormTransactionServiceWrapper with proper onSubmit */}
            <FormTransactionServiceWrapper
              ref={serviceFormRef}
              member={selectedChild}
              onSubmit={handleServiceForm} // // This is crucial - it handles the form submission
              hideAdvanceBooking={true}
            />
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
        <Button
          label="Cancel"
          severity="secondary"
          outlined
          onClick={() => onServiceAdded()}
          disabled={isCreating}
        />
        <Button
          label={isCreating ? "Adding..." : "Add Service"}
          disabled={!selectedChild || isCreating || childrenList.length === 0}
          loading={isCreating}
          onClick={handleTriggerSubmit}
        />
      </div>
    </div>
  );
}
